import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
  ListItem,
  ListItemIcon,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  alpha,
  keyframes,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { ChevronLeft, Menu as MenuIcon } from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../utils/auth/msalConfig";
import { useNavigate } from "react-router-dom";
import { adminService, tourService } from "../services";
import TutorialDialog from "./TutorialDialog";

// Mock data for checking admin status - in a real app, this would come from your backend
const mockAdminUsers = [
  'rohan.devagiri@ethosenergy.com',
  "indravardhan.moram@ethosenergy.com"
];

// Helper function to check if user is admin
function isUserAdmin(userEmail) {
  if (!userEmail) return false;
  return mockAdminUsers.includes(userEmail.toLowerCase());
}

// CSS animation for spinning icon
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// CSS animation for pulsing update icon
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
`;

// Date utility function
const isToday = (date) => {
  const today = new Date();
  const compareDate = new Date(date);
  return (
    compareDate.getDate() === today.getDate() &&
    compareDate.getMonth() === today.getMonth() &&
    compareDate.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const compareDate = new Date(date);
  return (
    compareDate.getDate() === yesterday.getDate() &&
    compareDate.getMonth() === yesterday.getMonth() &&
    compareDate.getFullYear() === yesterday.getFullYear()
  );
};

const formatDate = (date) => {
  const options = { weekday: 'long', month: 'long', day: 'numeric' };
  return new Date(date).toLocaleDateString(undefined, options);
};

export default function Sidebar({
  sessions,
  activeId,
  onNewSession,
  onSelectSession,
  isMobile,
  handleSidebarToggle,
  sidebarOpen,
  setSidebarOpen,
  isAuthenticated,
  handleSignIn,
  userName,
  versionChecker,
  isChatLoading
}) {  // Menu state for session options
  const { instance, accounts } = useMsal();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const userUpn = accounts.length > 0 ? accounts[0].username || accounts[0].homeAccountId?.split('.')[0] : "";
    const userEmail = accounts.length > 0 ? accounts[0].username || accounts[0].homeAccountId?.split('.')[0] : "";
  // State for admin access control
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(false);
  
  // State for tutorial dialog
  const [tutorialOpen, setTutorialOpen] = useState(false);

  // Check if menu is open
  const isMenuOpen = Boolean(menuAnchorEl);

  // Get MSAL instance and authentication methods
  const navigate = useNavigate();
  
  // Get user email for admin access check

  // Open the menu when clicking on the dots
  const handleMenuOpen = (event, sessionId) => {
    event.stopPropagation(); // Prevent triggering session selection
    setCurrentSessionId(sessionId);
    setMenuAnchorEl(event.currentTarget);
  };

  // Close the menu
  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation(); // Prevent triggering session selection
    }
    setMenuAnchorEl(null);
  };

  // Copy session ID to clipboard
  const handleCopySessionId = (event) => {
    event.stopPropagation(); // Prevent triggering session selection
    if (currentSessionId) {
      navigator.clipboard.writeText(currentSessionId)
        .then(() => {
          setSnackbarOpen(true);
          handleMenuClose();
        })
        .catch((error) => {
          console.error("Failed to copy session ID:", error);
        });
    }
  };
  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };  // Check admin access function using local logic (same as App.js)
  const checkAdminAccess = async () => {
    if (!isAuthenticated || !userEmail) {
      setHasAdminAccess(false);
      setAdminCheckLoading(false);
      return;
    }

    try {
      setAdminCheckLoading(true);
      
      // Allow all authenticated users to access admin portal
      const hasAccess = true; // Allow all authenticated users
      console.log('Sidebar admin check:', { userEmail, hasAccess: true, note: 'All authenticated users can access admin' });
      setHasAdminAccess(hasAccess);
      
    } catch (error) {
      console.error('Error checking admin access:', error);
      // Still allow all authenticated users even if there's an error
      setHasAdminAccess(true);
    } finally {
      setAdminCheckLoading(false);
    }
  };
  
  // Get display name for a session
  const getSessionDisplayName = (session) => {
    const MAX_LEN = 27;
    const ELLIPSIS = '…'; // or '...'
  
    // 1. Guard against null/undefined session
    if (!session) {
      return 'Unknown session';
    }
  
    // 2. Try to use title, if it's a non-empty string
    const rawTitle = typeof session.title === 'string' ? session.title.trim() : '';
    if (rawTitle) {
      return rawTitle.length > MAX_LEN
        ? rawTitle.slice(0, MAX_LEN) + ELLIPSIS
        : rawTitle;
    }
  
    // 3. Fallback to id, if present
    const rawId = session.id != null ? String(session.id).trim() : '';
    if (rawId) {
      return rawId.length > MAX_LEN
        ? rawId.slice(0, MAX_LEN) + ELLIPSIS
        : rawId;
    }
  
    // 4. Last resort
    return 'Unknown session';
  };
  

  // Group sessions by date
  const groupedSessions = useMemo(() => {
    const groups = {
      today: [],
      yesterday: [],
      older: {}
    };

    sessions.forEach(session => {
      // Use lastModified if available and not null, otherwise fall back to createdAt
      const date = (session.lastModified && session.lastModified !== null) 
        ? session.lastModified 
        : (session.createdAt || new Date().toISOString());

      if (isToday(date)) {
        groups.today.push(session);
      } else if (isYesterday(date)) {
        groups.yesterday.push(session);
      } else {
        const formattedDate = formatDate(date);
        if (!groups.older[formattedDate]) {
          groups.older[formattedDate] = [];
        }
        groups.older[formattedDate].push(session);
      }
    });

    return groups;
  }, [sessions]);

  // Use the handleSignIn from props instead of defining a new one
  /*
  const handleSignIn = () => {
    instance.loginPopup(loginRequest).catch(error => {
      console.error("Sign in failed:", error);
    });
  };
  */

  // Handle sign out
  const handleSignOut = () => {
    try {
      // Clear session storage
      sessionStorage.clear();
      // Log out using MSAL
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Sign out failed:", error);
      window.location.reload();
    }
  };  // Check admin access on component mount and when authentication status changes
  useEffect(() => {
    if (isAuthenticated && userEmail) {
      checkAdminAccess();
    } else {
      setHasAdminAccess(false);
    }
  }, [isAuthenticated, userEmail]); // Removed checkAdminAccess from dependencies to avoid infinite loop

  // Tour and tutorial handlers
  const handleStartTour = () => {
    tourService.startMainTour();
  };

  const handleOpenTutorial = () => {
    setTutorialOpen(true);
  };

  const handleCloseTutorial = () => {
    setTutorialOpen(false);
  };
  return (
    <Box
      component="nav"
      data-tour="sidebar"
      sx={{
        width: 300,
        height: "100vh",
        bgcolor: "background.sidebar",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          gap: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          <img src="/ethos.svg" alt="Logo" width={30} height={30} />
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
            Parts360
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {!isMobile && (
            <IconButton
              onClick={() => setSidebarOpen(false)}
              sx={{
                zIndex: 1200,
              }}
            >
              <Tooltip title="Close Sidebar" arrow>
                {sidebarOpen ? <Icon icon="hugeicons:sidebar-left-01" width="20px" height="20px" /> : <MenuIcon />}
              </Tooltip>
            </IconButton>
          )}          <IconButton
            sx={{
              zIndex: 1200,
            }}
            data-tour="new-session"
            onClick={onNewSession}>
            <Tooltip title="New Session" arrow>
              <Icon icon="solar:pen-new-square-linear" width="20px" height="20px" />
            </Tooltip>
          </IconButton>
        </Box>
      </Box>
      <Divider />      {/* Session List */}
      <List data-tour="session-list" sx={{
        flexGrow: 1, overflowY: "auto", px: 1,
        // no scrollbar
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&:hover::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
        },
      }}>
        <Typography variant="overline" color="text.secondary" sx={{ px: 1, fontSize: '14px', textTransform: 'none', fontWeight: "bold", mt: 2, mb: 1 }}>
          Session Chats 
        </Typography>

        {/* Today's Sessions */}
        {groupedSessions.today.length > 0 && (
          <>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1, fontSize: '12px', textTransform: 'none', fontWeight: 'normal', mt: 2, mb: 1 }}>
                Today
              </Typography>
            </Box>
            {groupedSessions.today.map((session) => (
              <ListItemButton
                key={session.id}
                selected={session.id === activeId}
                onClick={() => onSelectSession(session.id)}
                sx={{ borderRadius: "10px", mb: 0, p: 0.7 }}
              >
                <ListItemText
                  primary={`${getSessionDisplayName(session)}`}
                />
                <ListItemIcon sx={{ minWidth: 'auto', ml: 'auto', mr: 'auto' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      padding: '4px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: '#2065D1',
                        boxShadow: '0 2px 6px rgba(32, 101, 209, 0.4)',
                        color: 'white',
                      },
                    }}
                    onClick={(e) => handleMenuOpen(e, session.id)}
                  >
                    <Icon icon="tabler:dots" width="20" height="20" />
                  </Box>
                </ListItemIcon>
              </ListItemButton>
            ))}
          </>
        )}

        {/* Yesterday's Sessions */}
        {groupedSessions.yesterday.length > 0 && (
          <>
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1, fontSize: '12px', textTransform: 'none', fontWeight: 'normal', mb: 1 }}>
                Yesterday
              </Typography>
            </Box>
            {groupedSessions.yesterday.map((session) => (
              <ListItemButton
                key={session.id}
                selected={session.id === activeId}
                onClick={() => onSelectSession(session.id)}
                sx={{ borderRadius: "10px", mb: 0, p: 0.7 }}
              >
                <ListItemText
                  primary={`${getSessionDisplayName(session)}`}
                />
                <ListItemIcon sx={{ minWidth: 'auto', ml: 'auto', mr: 'auto' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      padding: '4px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: '#2065D1',
                        boxShadow: '0 2px 6px rgba(32, 101, 209, 0.4)',
                        color: 'white',
                      },
                    }}
                    onClick={(e) => handleMenuOpen(e, session.id)}
                  >
                    <Icon icon="tabler:dots" width="20" height="20" />
                  </Box>
                </ListItemIcon>
              </ListItemButton>
            ))}
          </>
        )}

        {/* Older Sessions */}
        {Object.keys(groupedSessions.older).map((date) => (
          <React.Fragment key={date}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1, fontSize: '12px', textTransform: 'none', fontWeight: 'normal', mb: 1 }}>
                {date}
              </Typography>
            </Box>
            {groupedSessions.older[date].map((session) => (
              <ListItemButton
                key={session.id}
                selected={session.id === activeId}
                onClick={() => onSelectSession(session.id)}
                sx={{ borderRadius: "10px", mb: 0, p: 0.7 }}
              >
                <ListItemText
                  primary={`${getSessionDisplayName(session)}`}
                />
                <ListItemIcon sx={{ minWidth: 'auto', ml: 'auto', mr: 'auto' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      padding: '4px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: '#2065D1',
                        boxShadow: '0 2px 6px rgba(32, 101, 209, 0.4)',
                        color: 'white',
                      },
                    }}
                    onClick={(e) => handleMenuOpen(e, session.id)}
                  >
                    <Icon icon="tabler:dots" width="20" height="20" />
                  </Box>
                </ListItemIcon>
              </ListItemButton>
            ))}
          </React.Fragment>
        ))}
      </List>

      <Divider />      {/* Sign‑in Prompt */}
      <Box data-tour="auth-section" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {isAuthenticated
            ? `Signed in as ${accounts[0]?.name || accounts[0]?.username}`
            : "Sign in to sync your sessions across devices."}
        </Typography>
        <Button
          fullWidth
          variant="contained"
          sx={{ backgroundColor: "background.paper", color: "text.primary", borderRadius: "10px" }}
          startIcon={<Icon icon={isAuthenticated ? "fluent:person-24-filled" : "fluent:person-24-regular"} />}
          onClick={isAuthenticated ? handleSignOut : handleSignIn}
        >
          {isAuthenticated ? "Sign out" : "Sign in"}
        </Button>        
        
        {/* Admin Portal Link - Only shown to authenticated users with admin access */}
        {isAuthenticated && hasAdminAccess && !adminCheckLoading && (
          <Button
            fullWidth
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: "10px",
              borderColor: "#2065D1",
              color: "#2065D1",
              '&:hover': {
                backgroundColor: (theme) => alpha("#2065D1", 0.1),
                borderColor: "#2065D1"
              }
            }}
            startIcon={<Icon icon="carbon:user-admin" />}
            onClick={() => {
              console.log('Admin Portal button clicked, navigating to /admin');
              window.location.href = '/admin';
            }}
          >
            Admin Portal
          </Button>
        )}

        {/* Tour, Tutorial, and Update Buttons Row */}
        <Box data-tour="tour-buttons" sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              borderRadius: "8px",
              borderColor: "#4caf50",
              color: "#4caf50",
              fontSize: "0.75rem",
              py: 0.5,
              position: 'relative',
              '&:hover': {
                backgroundColor: (theme) => alpha("#4caf50", 0.1),
                borderColor: "#4caf50"
              }
            }}
            startIcon={<Icon icon="material-symbols:tour" width={16} height={16} />}
            onClick={handleStartTour}
          >
            Quick Tour
            {/* Update indicator dot */}
            {versionChecker && versionChecker.updateAvailable && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#f44336',
                  border: '1px solid white'
                }}
              />
            )}
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              borderRadius: "8px",
              borderColor: "#ff9800",
              color: "#ff9800",
              fontSize: "0.75rem",
              py: 0.5,
              '&:hover': {
                backgroundColor: (theme) => alpha("#ff9800", 0.1),
                borderColor: "#ff9800"
              }
            }}
            startIcon={<Icon icon="material-symbols:school" width={16} height={16} />}
            onClick={handleOpenTutorial}
          >
            Tutorial
          </Button>
          
          {/* Update Sync Button - Only shows when update is available */}
          {versionChecker && versionChecker.updateAvailable && (
            <Tooltip 
              title={`Update available: ${versionChecker.currentVersion} → ${versionChecker.serverVersion}`}
              placement="top"
              arrow
            >
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  size="small"
                  onClick={() => versionChecker.handleReload(isChatLoading)}
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #2196f3",
                    color: "#2196f3",
                    width: 32,
                    height: 32,
                    animation: `${pulse} 2s infinite`,
                    '&:hover': {
                      backgroundColor: (theme) => alpha("#2196f3", 0.1),
                      borderColor: "#2196f3",
                      transform: 'scale(1.05)',
                      animation: 'none' // Stop pulsing on hover
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Icon 
                    icon="material-symbols:sync" 
                    width={16} 
                    height={16}
                  />
                </IconButton>
                {/* Red dot indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#f44336',
                    border: '1px solid white',
                    animation: `${pulse} 1.5s infinite`
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
          {/* Debug: Show admin access status */}
        {/* {isAuthenticated  && hasAdminAccess && !adminCheckLoading  && (
          <Button
            fullWidth
            variant="contained"
            sx={{
              mt: 1,
              borderRadius: "10px",
              bgcolor: "#ff6b6b",
              color: "white",
              '&:hover': {
                bgcolor: "#ff5252"
              }
            }}
            startIcon={<Icon icon="mdi:test-tube" />}
            onClick={() => {
              console.log('Manual admin navigation clicked');
              console.log('Current user:', accounts[0]?.username || accounts[0]?.name);
              console.log('mockAdminUsers:', ['rohan.devagiri@ethosenergy.com', 'indravardhan.moram@ethosenergy.com']);
              console.log('Is admin check result:', isUserAdmin(accounts[0]?.username || accounts[0]?.name));
              window.location.href = '/admin';
            }}
          >
            Admin Portal
          </Button>
        )} */}
      </Box>

      {/* Session Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: '150px',
            bgcolor: 'background.paper',
            borderRadius: '10px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem
          onClick={handleCopySessionId}
          sx={{ borderRadius: '10px', py: 0.5, px: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <Icon icon="tabler:copy" width="20" height="20" />
          </ListItemIcon>
          <Typography variant="body2" color="text.primary">
            Copy Session ID
          </Typography>
        </MenuItem>
      </Menu>

      {/* Snackbar for copy confirmation */}      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Session ID copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Tutorial Dialog */}
      <TutorialDialog 
        open={tutorialOpen} 
        onClose={handleCloseTutorial} 
      />
    </Box>
  );
}